import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Download, History, Pencil, Trash2, TriangleAlert } from "lucide-react";
import ListItemCardCst from "@components/ListItemCardCst";
import {
  ListItemAction,
  ListItemBadge,
} from "@components/ListItemCardCst/types";
import { EnrolledUserItem } from "../types";
import { formatEnrollTypeLabel } from "../config/enrollTypeMap";
import { ActivityV1Type } from "../../AddActivity/types";

interface UserEnrollmentRowProps {
  user: EnrolledUserItem;
  activityType?: ActivityV1Type;
  onDetails?: (user: EnrolledUserItem) => void;
  onEditEnrollment?: (user: EnrolledUserItem) => void;
  onDeleteEnrollment?: (user: EnrolledUserItem) => void;
  onEditGroupEnrollment?: (user: EnrolledUserItem) => void;
  onDeleteGroupEnrollment?: (user: EnrolledUserItem) => void;
  onHistory?: (user: EnrolledUserItem) => void;
  onDownloadCertificate?: (user: EnrolledUserItem) => void;
}

const NO_EXPIRATION_SENTINEL = "9999-12-31";

const STATUS_KEY_BY_CODE: Record<string, string> = {
  R: "refused",
  T: "top-down-needed",
  PW: "waiting-enrollment-request",
  PA: "waiting-self-enrollment",
};

const formatDate = (iso?: string | null): string =>
  iso && iso !== NO_EXPIRATION_SENTINEL ? dayjs(iso).format("DD/MM/YYYY") : "-";

const UserEnrollmentRow: React.FC<UserEnrollmentRowProps> = ({
  user,
  activityType,
  onDetails,
  onEditEnrollment,
  onDeleteEnrollment,
  onEditGroupEnrollment,
  onDeleteGroupEnrollment,
  onHistory,
  onDownloadCertificate,
}) => {
  const { t } = useTranslation("users-and-groups");
  const fullName =
    [user.userFirstName, user.userLastName].filter(Boolean).join(" ").trim() ||
    "-";

  const statusKey =
    (user.status && STATUS_KEY_BY_CODE[user.status]) ||
    (user.completeDate
      ? "completed"
      : (user.progPerc ?? 0) > 0
        ? "in-progress"
        : "to-start");

  const badges: ListItemBadge[] = [
    { label: formatEnrollTypeLabel(user.enrollType) },
    {
      label: String(t(`card.status.${statusKey}`)),
      variant:
        statusKey === "refused"
          ? "error"
          : statusKey === "top-down-needed"
            ? "warning"
            : statusKey === "completed"
              ? "success"
              : statusKey === "in-progress"
                ? "info"
                : "default",
    },
    ...(user.mandatory
      ? [
          {
            variant: "warning" as const,
            icon: <TriangleAlert size={14} />,
            label: "",
          },
        ]
      : []),
  ];

  const fromGroup = user.massiveGroupId != null;

  const moreActions: ListItemAction[][] = onDeleteEnrollment
    ? [
        [
          {
            label: String(t("card.actions.edit-enrollment")),
            icon: <Pencil size={14} />,
            onClick: () => onEditEnrollment?.(user),
            disabled: !onEditEnrollment,
          },
          {
            label: String(t("card.actions.delete-enrollment")),
            icon: <Trash2 size={14} />,
            onClick: () => onDeleteEnrollment(user),
          },
        ],
        ...(fromGroup
          ? [
              [
                {
                  label: String(t("card.actions.edit-group-enrollment")),
                  icon: <Pencil size={14} />,
                  onClick: () => onEditGroupEnrollment?.(user),
                  disabled: !onEditGroupEnrollment || !user.groupType,
                },
                {
                  label: String(t("card.actions.delete-group-enrollment")),
                  icon: <Trash2 size={14} />,
                  onClick: () => onDeleteGroupEnrollment?.(user),
                  disabled: !onDeleteGroupEnrollment || !user.groupType,
                },
              ],
            ]
          : []),
        [
          {
            label: String(t("card.actions.enrollment-history")),
            icon: <History size={14} />,
            onClick: () => onHistory?.(user),
            disabled: !onHistory,
          },
        ],
        [
          {
            label: String(t("card.actions.download-certificate")),
            icon: <Download size={14} />,
            onClick: () => onDownloadCertificate?.(user),
            disabled: !onDownloadCertificate || user.certificateId == null,
          },
        ],
      ]
    : [];

  return (
    <ListItemCardCst
      title={fullName}
      subtitle={{
        id: user.userId,
        email: user.userEmail,
        groupId: fromGroup ? String(user.massiveGroupId) : undefined,
        sessionIdEnrolled: user.editionId,
      }}
      details={{
        row1: `${t("card.completion-date")}: ${formatDate(user.completeDate)}`,
        row2: `${t("card.expiration-date")}: ${formatDate(user.expirationDate)}`,
      }}
      badges={badges}
      actions={
        onDetails
          ? [
              {
                label: String(t("card.details")),
                variant: "outlined",
                onClick: () => onDetails(user),
              },
            ]
          : undefined
      }
      moreActions={moreActions}
      activityType={activityType}
    />
  );
};

export default UserEnrollmentRow;
